<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import agents from '../../../../api/agents';
import Button from '../../../../components-next/button/Button.vue';
import Input from '../../../../components-next/input/Input.vue';
import Select from '../../../../components-next/select/Select.vue';

const props = defineProps({
  currentStage: {
    type: String,
    default: '',
  },
});

// eslint-disable-next-line vue/custom-event-name-casing -- consumers use @items-created (kebab-case in template); rename would cascade to PipelineHeader.vue
const emit = defineEmits(['close', 'items-created']);
const { t } = useI18n();
const store = useStore();
const { showAlert } = useAlert();

const loading = ref(false);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const itemsList = ref([]);
const agentsList = ref([]);
const loadingAgents = ref(false);
const agentsLoadFailed = ref(false);

// Form para novo item
const newItem = ref({
  title: '',
  priority: 'none',
  agent_id: '',
});

// Prioridades disponíveis
const priorityOptions = [
  { id: 'none', name: t('PIPELINE.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('PIPELINE.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('PIPELINE.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('PIPELINE.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('PIPELINE.PRIORITY_LABELS.URGENT') },
];
const prioritySelectOptions = priorityOptions.map(option => ({
  value: option.id,
  label: option.name,
}));

const stages = computed(() => {
  if (!selectedPipeline.value?.stages) return [];

  return Object.entries(selectedPipeline.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});
const stageOptions = computed(() =>
  stages.value.map(stage => ({ value: stage.id, label: stage.name }))
);
const agentOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.AGENT.PLACEHOLDER'),
  },
  ...agentsList.value.map(agent => ({ value: agent.id, label: agent.name })),
]);

const selectedStage = ref(props.currentStage || '');

// Função para carregar os agentes
const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    agentsLoadFailed.value = false;
    const { data } = await agents.get();
    agentsList.value = data;
  } catch {
    agentsLoadFailed.value = true;
  } finally {
    loadingAgents.value = false;
  }
};

const addItemToList = () => {
  if (!newItem.value.title.trim()) return;

  const desiredPosition =
    Math.max(0, ...itemsList.value.map(item => item.desiredPosition || 0)) + 1;

  itemsList.value.push({
    ...newItem.value,
    id: Date.now(), // ID temporário para a lista
    desiredPosition,
  });

  // Limpa o form
  newItem.value = {
    title: '',
    priority: 'none',
    agent_id: '',
  };
};

const removeItemFromList = itemId => {
  itemsList.value = itemsList.value.filter(item => item.id !== itemId);
};

const handleSubmit = async () => {
  if (loading.value || !itemsList.value.length || !selectedStage.value) return;

  try {
    loading.value = true;
    const pendingItems = [...itemsList.value];
    const payloads = pendingItems.map(item => ({
      pipeline_id: Number(selectedPipeline.value.id),
      pipeline_stage: selectedStage.value,
      position: item.desiredPosition,
      priority: item.priority,
      item_details: {
        title: item.title,
        priority: item.priority,
        agent_id: item.agent_id || null,
      },
    }));

    const results = await Promise.allSettled(
      payloads.map(item => PipelineCardsAPI.createCard(item))
    );
    const createdItems = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);
    const failedItems = pendingItems.filter(
      (_, index) => results[index].status === 'rejected'
    );
    itemsList.value = failedItems;

    if (createdItems.length > 0) {
      await store.dispatch('pipelineCards/itemUpdated');

      /* eslint-disable vue/custom-event-name-casing */
      emit(
        'items-created',
        createdItems.map(response => response.data)
      );
      /* eslint-enable vue/custom-event-name-casing */
    }

    if (failedItems.length > 0) {
      showAlert(
        t('PIPELINE.MESSAGES.BULK_CREATE_PARTIAL', {
          created: createdItems.length,
          failed: failedItems.length,
        })
      );
      return;
    }

    emit('close');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[BulkAddModal] handleSubmit error:', error);
    showAlert(t('PIPELINE.MESSAGES.BULK_CREATE_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchAgents();
});
</script>

<template>
  <div class="w-full max-w-[800px]">
    <div class="p-4 sm:p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.ADD_ITEM') }}
      </h3>

      <div class="mb-4">
        <label
          for="bulk-add-stage"
          class="block text-label text-n-slate-12 mb-2"
        >
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.LABEL') }}
        </label>
        <Select
          v-model="selectedStage"
          input-id="bulk-add-stage"
          name="bulk_add_stage"
          class="w-full [&>select]:w-full"
          :options="stageOptions"
          :placeholder="t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.PLACEHOLDER')"
          :aria-label="t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.LABEL')"
          aria-required
        />
      </div>

      <!-- Formulário de novo item -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div class="col-span-3">
          <label
            for="bulk-add-title"
            class="block text-label text-n-slate-12 mb-2"
          >
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.TITLE.LABEL') }}
          </label>
          <Input
            id="bulk-add-title"
            v-model="newItem.title"
            name="bulk_add_title"
            type="text"
            autocomplete="off"
            class="w-full"
            custom-input-class="!h-11"
            :placeholder="
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.TITLE.PLACEHOLDER')
            "
          />
        </div>

        <div>
          <label
            for="bulk-add-priority"
            class="block text-label text-n-slate-12 mb-2"
          >
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.PRIORITY.LABEL') }}
          </label>
          <Select
            v-model="newItem.priority"
            input-id="bulk-add-priority"
            name="bulk_add_priority"
            class="w-full [&>select]:w-full"
            :options="prioritySelectOptions"
            :aria-label="
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.PRIORITY.LABEL')
            "
          />
        </div>

        <div class="sm:col-span-2">
          <label
            for="bulk-add-agent"
            class="block text-label text-n-slate-12 mb-2"
          >
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.AGENT.LABEL') }}
          </label>
          <Select
            v-model="newItem.agent_id"
            input-id="bulk-add-agent"
            name="bulk_add_agent"
            class="w-full [&>select]:w-full"
            :options="agentOptions"
            :aria-label="
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.AGENT.LABEL')
            "
            :disabled="loadingAgents"
          />
          <div
            v-if="agentsLoadFailed"
            role="alert"
            class="mt-2 flex flex-wrap items-center gap-2 text-sm text-n-ruby-11"
          >
            <span>{{ t('PIPELINE.MESSAGES.AGENTS_FETCH_ERROR') }}</span>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              class="min-h-11"
              :disabled="loadingAgents"
              @click="fetchAgents"
            >
              {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.RETRY') }}
            </Button>
          </div>
        </div>

        <div class="col-span-3">
          <Button
            variant="solid"
            color="blue"
            size="sm"
            class="min-h-11"
            :disabled="!newItem.title.trim()"
            @click="addItemToList"
          >
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.ADD_TO_LIST') }}
          </Button>
        </div>
      </div>

      <!-- Lista de itens -->
      <div
        v-if="itemsList.length"
        class="mb-4 border border-n-weak rounded-lg overflow-hidden max-h-[300px] overflow-y-auto"
      >
        <div
          v-for="(item, index) in itemsList"
          :key="item.id"
          class="flex items-center justify-between p-3 hover:bg-n-alpha-1"
          :class="index < itemsList.length - 1 && 'border-b border-n-weak'"
        >
          <div class="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
            <span class="min-w-0 break-words text-body-main text-n-slate-12">{{
              item.title
            }}</span>
            <span
              class="px-2 py-1 text-label-small font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ t(`PIPELINE.PRIORITY_LABELS.${item.priority.toUpperCase()}`) }}
            </span>
            <span v-if="item.agent_id" class="text-label text-n-slate-11">
              {{ agentsList.find(a => a.id === item.agent_id)?.name }}
            </span>
          </div>
          <Button
            variant="ghost"
            color="ruby"
            size="sm"
            class="min-h-11 min-w-11"
            :aria-label="
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.REMOVE_ITEM')
            "
            @click="removeItemFromList(item.id)"
          >
            <template #icon>
              <fluent-icon icon="delete" size="16" />
            </template>
          </Button>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          class="min-h-11"
          :disabled="loading"
          @click="$emit('close')"
        >
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          class="min-h-11"
          :is-loading="loading"
          :disabled="loading || !itemsList.length || !selectedStage"
          @click="handleSubmit"
        >
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.CONFIRM') }}
        </Button>
      </div>
    </div>
  </div>
</template>
